/* eslint-disable no-console */
import { IAnalyticEvent } from './events/analytics';
import { AmplitudeProvider } from './providers/amplitude';
import { IntercomProvider } from './providers/intercom';
import { HubSpotProvider } from './providers/hubspot';
import { GTMProvider } from './providers/gtm';

export interface TrackProps {
  orgId?: string;
  userId?: string;
  eventId?: string;
  [key: string]: any;
}

export interface IdentityPayload {
  userId: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  properties?: Record<string, any>;
}

export interface InitOptions {
  amplitudeApiKey?: string;
  intercomAppId?: string;
  withHubspot?: boolean;
  gtmInstance?: unknown;
  environment: 'development' | 'staging' | 'production';
  debug?: boolean;
}

export interface AnalyticsProvider {
  init(options: any): Promise<void> | void;
  identify(identity: IdentityPayload): void;
  track(event: string, properties?: TrackProps): void;
  isReady(): boolean;
  startSessionRecording(): void;
  stopSessionRecording(): void;
}

export default class AnalyticsService {
  public providers: AnalyticsProvider[] = [];
  private isInitialized = false;
  private currentIdentity: IdentityPayload | null = null;
  private debugMode = false;
  private sessionRecordingActive = false;
  private cookieDomain = 'localhost';

  private readonly SESSION_RECORDING_COOKIE = 'fw_session_recording';

  /**
   * Initializes the analytics service with the provided configuration options
   * @param options - Configuration options for various analytics providers
   */
  public init(options: InitOptions): void {
    try {
      this.debugMode = options.debug || false;
      this.cookieDomain = options.environment === 'development' ? 'localhost' : '.fourwaves.com';

      if (options.amplitudeApiKey) {
        const amplitude = new AmplitudeProvider();
        amplitude.init({ apiKey: options.amplitudeApiKey, debug: this.debugMode });
        this.providers.push(amplitude);
      }

      if (options.intercomAppId) {
        const intercom = new IntercomProvider();
        intercom.init({ appId: options.intercomAppId, debug: this.debugMode });
        this.providers.push(intercom);
      }

      if (options.withHubspot) {
        const hubspot = new HubSpotProvider();
        hubspot.init({ debug: this.debugMode });
        this.providers.push(hubspot);
      }

      if (options.gtmInstance) {
        const gtm = new GTMProvider();
        gtm.init({ gtmInstance: options.gtmInstance, debug: this.debugMode });
        this.providers.push(gtm);
      }

      this.isInitialized = true;

      if (this.debugMode) console.debug('Analytics service initialized with providers:', this.providers.length);

      if (this.hasSessionRecordingCookie()) {
        // Session recording should not be enabled automatically for all users because of quota limits
        // Wait for 1 second to ensure providers are ready
        setTimeout(() => this.startSessionRecording(), 1000);
      }
    } catch (error) {
      this.isInitialized = true;
    }
  }

  /**
   * Identifies a user across all analytics providers
   * @param identity - User identity information including userId, email, and optional name
   */
  public identify(identity: IdentityPayload): void {
    if (!this.isInitialized) return;

    try {
      this.currentIdentity = identity;
      this.providers.filter(provider => provider.isReady()).forEach(provider => provider.identify(identity));
      if (this.debugMode) console.debug('Analytics: User identified:', identity.userId);
    } catch (error) {
      console.error('Analytics identify failed:', error);
    }
  }

  /**
   * Track an analytics event
   * @param event - Event class instance implementing IAnalyticEvent interface
   */
  public track(event: IAnalyticEvent): void {
    if (!this.isInitialized) return;

    try {
      if (this.currentIdentity) event.properties.userId = event.properties.userId || this.currentIdentity.userId;

      this.providers
        .filter(provider => provider.isReady())
        .forEach(provider => provider.track(event.name, event.properties));

      if (this.debugMode) console.debug('Analytics: Event tracked:', event.name, event.properties);
    } catch (error) {
      console.error('Analytics track failed:', error);
    }
  }

  /**
   * Checks if the analytics service is initialized
   * @returns true if initialized, false otherwise
   */
  public isReady(): boolean {
    return this.isInitialized;
  }

  /**
   * Starts session recording for all providers
   */
  public startSessionRecording(): void {
    if (!this.isInitialized || this.sessionRecordingActive) return;

    try {
      this.sessionRecordingActive = true;
      this.providers.filter(provider => provider.isReady()).forEach(provider => provider.startSessionRecording());

      if (this.debugMode) console.debug('Analytics: Session recording started');
    } catch (error) {
      console.error('Analytics startSessionRecording failed:', error);
    }
  }

  /**
   * Stops session recording for all providers
   */
  public stopSessionRecording(): void {
    if (!this.isInitialized || !this.sessionRecordingActive) return;

    try {
      this.sessionRecordingActive = false;
      this.providers.filter(provider => provider.isReady()).forEach(provider => provider.stopSessionRecording());

      if (this.debugMode) console.debug('Analytics: Session recording stopped');
    } catch (error) {
      console.error('Analytics stopSessionRecording failed:', error);
    }
  }

  /**
   * Sets the session recording cookie with a 90 days expiration to persist session recording across visits
   */
  public setSessionRecordingCookie(): void {
    try {
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + 90); // expires in 90 days
      const secureFlag = this.cookieDomain !== 'localhost' ? '; Secure' : '';
      document.cookie = `${this.SESSION_RECORDING_COOKIE}=true; expires=${expiryDate.toUTCString()}; path=/; SameSite=Lax; domain=${this.cookieDomain}${secureFlag}`;
      if (this.debugMode) console.debug('Analytics: Session recording cookie set');
    } catch (error) {
      console.error('Analytics setSessionRecordingCookie failed:', error);
    }
  }

  /**
   * Checks if the session recording cookie exists
   * @returns true if cookie exists, false otherwise
   */
  private hasSessionRecordingCookie(): boolean {
    try {
      return new RegExp(`(^|;\\s*)${this.SESSION_RECORDING_COOKIE}=`).test(document.cookie);
    } catch (error) {
      return false;
    }
  }
}
